import { CommonModule, DOCUMENT } from '@angular/common';
import {
    AfterViewChecked,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    ElementRef,
    HostListener,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewChild
} from '@angular/core';

@Component({
    selector: 'app-full-screen-loader',
    templateUrl: './full-screen-loader.component.html',
    styleUrls: ['./full-screen-loader.component.scss'],
    imports: [CommonModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class FullScreenLoaderComponent implements OnChanges, OnDestroy, AfterViewChecked {
    @Input() active = false;
    @Input() message = '';

    @ViewChild('overlay') private readonly overlay?: ElementRef<HTMLElement>;

    private previouslyFocusedElement: HTMLElement | null = null;
    private shouldFocusOverlay = false;
    private scrollLock: {
        overflow: string;
        position: string;
        top: string;
        width: string;
        scrollY: number;
    } | null = null;

    constructor(@Inject(DOCUMENT) private readonly document: Document) {}

    @HostListener('document:keydown', ['$event'])
    onDocumentKeydown(event: KeyboardEvent): void {
        this.preventEventWhileActive(event);
    }

    @HostListener('document:wheel', ['$event'])
    @HostListener('document:touchmove', ['$event'])
    onDocumentScroll(event: Event): void {
        this.preventEventWhileActive(event);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes.active) {
            return;
        }

        if (this.active) {
            this.previouslyFocusedElement = this.document.activeElement instanceof HTMLElement
                ? this.document.activeElement
                : null;
            this.shouldFocusOverlay = true;
            this.lockScroll();
            return;
        }

        this.restoreFocus();
        this.unlockScroll();
    }

    ngOnDestroy(): void {
        this.unlockScroll();
    }

    ngAfterViewChecked(): void {
        if (!this.shouldFocusOverlay || !this.overlay?.nativeElement) {
            return;
        }

        this.overlay.nativeElement.focus();
        this.shouldFocusOverlay = false;
    }

    private restoreFocus(): void {
        if (this.previouslyFocusedElement?.isConnected) {
            this.previouslyFocusedElement.focus();
        }

        this.previouslyFocusedElement = null;
        this.shouldFocusOverlay = false;
    }

    private preventEventWhileActive(event: Event): void {
        if (!this.active) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
    }

    private lockScroll(): void {
        if (this.scrollLock) {
            return;
        }

        const body = this.document.body;
        const scrollY = this.document.defaultView?.scrollY ?? 0;

        this.scrollLock = {
            overflow: body.style.overflow,
            position: body.style.position,
            top: body.style.top,
            width: body.style.width,
            scrollY
        };

        body.style.overflow = 'hidden';
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.width = '100%';
    }

    private unlockScroll(): void {
        if (!this.scrollLock) {
            return;
        }

        const body = this.document.body;
        const scrollY = this.scrollLock.scrollY;

        body.style.overflow = this.scrollLock.overflow;
        body.style.position = this.scrollLock.position;
        body.style.top = this.scrollLock.top;
        body.style.width = this.scrollLock.width;
        this.scrollLock = null;
        this.document.defaultView?.scrollTo(0, scrollY);
    }
}
